import React, { useState, useRef, useCallback } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonPage,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonList,
  IonItem,
  IonLabel,
  IonToggle,
  IonBadge,
  IonIcon,
  IonButton,
  IonGrid,
  useIonRouter
} from '@ionic/react';
import { useAuth } from '../contexts';
import {
  chevronBack,
  notificationsOutline,
  mailOutline,
  phonePortraitOutline,
  volumeHighOutline,
  phonePortraitSharp,
  calendar,
  cash,
  people,
  documentText,
  warning,
  receiptSharp,
  closeOutline,
  listOutline,
} from 'ionicons/icons';
import styles from './NotificationSettings.module.scss';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import { requestNotificationPermission } from '../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

interface NotificationSettingsProps {
  onClose?: () => void;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const { authState } = useAuth();
  const { user } = authState;

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else if (router.canGoBack()) {
      router.goBack();
    } else {
      router.push('/tabs/manage');
    }
  }, [onClose, router]);

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const containerRef = useRef<HTMLIonGridElement>(null);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(containerRef, 66, applyProgress);

  // Notification preferences state — default true, overridden from Firestore on mount
  const notifPrefs = user?.preferences?.notifications;
  const [pushEnabled, setPushEnabled] = useState(notifPrefs?.push ?? true);
  const [emailEnabled, setEmailEnabled] = useState(notifPrefs?.email ?? true);
  const [soundEnabled, setSoundEnabled] = useState(notifPrefs?.sound ?? true);
  const [vibrationEnabled, setVibrationEnabled] = useState(notifPrefs?.vibration ?? true);

  // Category preferences
  const [bookingNotifs, setBookingNotifs] = useState(notifPrefs?.categories?.bookings ?? true);
  const [paymentNotifs, setPaymentNotifs] = useState(notifPrefs?.categories?.payments ?? true);
  const [teamNotifs, setTeamNotifs] = useState(notifPrefs?.categories?.team ?? true);
  const [formNotifs, setFormNotifs] = useState(notifPrefs?.categories?.forms ?? true);
  const [overdueNotifs, setOverdueNotifs] = useState(notifPrefs?.categories?.overdue ?? true);
  const [invoiceNotifs, setInvoiceNotifs] = useState(notifPrefs?.categories?.invoice ?? true);

  // Re-sync when user object loads (async auth) — computed during render
  // (React's "adjust state when a prop changes" pattern) rather than as
  // setState calls inside an effect.
  const [prevNotifPrefsSource, setPrevNotifPrefsSource] = useState(user?.preferences?.notifications);
  if (prevNotifPrefsSource !== user?.preferences?.notifications) {
    setPrevNotifPrefsSource(user?.preferences?.notifications);
    const p = user?.preferences?.notifications;
    if (p) {
      if (p.push !== undefined) setPushEnabled(p.push);
      if (p.email !== undefined) setEmailEnabled(p.email);
      if (p.sound !== undefined) setSoundEnabled(p.sound);
      if (p.vibration !== undefined) setVibrationEnabled(p.vibration);
      if (p.categories?.bookings !== undefined) setBookingNotifs(p.categories.bookings);
      if (p.categories?.payments !== undefined) setPaymentNotifs(p.categories.payments);
      if (p.categories?.team !== undefined) setTeamNotifs(p.categories.team);
      if (p.categories?.forms !== undefined) setFormNotifs(p.categories.forms);
      if (p.categories?.overdue !== undefined) setOverdueNotifs(p.categories.overdue);
      if (p.categories?.invoice !== undefined) setInvoiceNotifs(p.categories.invoice);
    }
  }

  const savePrefs = useCallback(async (patch: Record<string, unknown>) => {
    try {
      await (await import('@rsmaids/shared/services/DataServiceFactory')).DataServiceFactory
        .getService().auth.updateUserPreferences({ notifications: patch } as never);
    } catch (err) {
      console.error('[NotificationSettings] Failed to save prefs:', err);
    }
  }, []);

  // Initial value already reads Notification.permission via the lazy
  // initializer below, so a mount-time effect re-checking the same value
  // would always be redundant (browser permission dialogs are triggered
  // by handleRequestPermission below, which sets this directly on response).
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const handleRequestPermission = useCallback(async () => {
    if (!('Notification' in window)) return;
    const result = await requestNotificationPermission({ prompt: true });
    setBrowserPermission(result);
  }, []);

  const pageContent = (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={t('pages.NotificationSettings.notificationSettings')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        onClose ? (
          <IonButton shape='round' className={styles.closeButton} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </IonButton>
        ) : (
          <IonButton onClick={handleClose} shape='round' color='primary' aria-label={t('pages.NotificationSettings.closeSettings')} className={styles.backButton}>
            <IonIcon icon={chevronBack} />
          </IonButton>
        )
      }
        endButtons={
          <IonButton style={{ visibility: 'hidden' }}>
            <IonIcon icon={chevronBack} />
          </IonButton>
        }
      >
        <IonContent scrollY={false} className={styles.content}>
          <IonGrid
            ref={containerRef}
            className={styles.settingsContainer}
            onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
          >
            <div className={styles.titleCardWrapper}>
              <TitleCard
                ref={titleCardAnimationRef}
                title={t('pages.NotificationSettings.notificationSettings')}
                cardClassName={styles.settingsOverviewCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.settingsTitle}
              />
            </div>

            {/* Summary hero card */}
            <IonCard className={styles.sectionCard}>
              <IonCardContent className={styles.summaryCardBody}>
                <div className={styles.heroCompact}>
                  <div className={styles.iconCircle}>
                    <IonIcon icon={notificationsOutline} className={styles.circleIcon} />
                  </div>
                  <div className={styles.identityBlock}>
                    <div className={styles.heroRow}>
                      <h2 className={styles.heroLabel}>{t('pages.NotificationSettings.notificationStatus')}</h2>
                      <span className={styles.heroValue}>{pushEnabled ? t('pages.NotificationSettings.active') : t('pages.NotificationSettings.paused')}</span>
                    </div>
                    <p className={styles.heroDesc}>
                      {pushEnabled ? t('pages.NotificationSettings.pushAlertsEnabledOnThisDevice') : t('pages.NotificationSettings.pushAlertsAreCurrentlyDisabled')}
                    </p>
                  </div>
                </div>
              </IonCardContent>
            </IonCard>

            {/* Notification Channels */}
            <IonCard className={styles.sectionCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.sectionTitle}>
                  <IonIcon icon={notificationsOutline} className={styles.sectionIcon} />{t('pages.NotificationSettings.notificationChannels')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList className={styles.settingsList}>
                  <IonItem className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={phonePortraitOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.pushNotifications')}</h3>
                      <p>
                        {browserPermission === 'granted' && 'Receive notifications on this device'}
                        {browserPermission === 'denied' && 'Blocked — enable in browser settings'}
                        {browserPermission === 'default' && 'Permission required'}
                      </p>
                    </IonLabel>
                    {browserPermission === 'granted' ? (
                      <IonToggle
                        slot="end"
                        checked={pushEnabled}
                        onIonChange={(e) => { setPushEnabled(e.detail.checked); savePrefs({ push: e.detail.checked }); }}
                      />
                    ) : (
                      <IonBadge
                        slot="end"
                        className={styles.enableBadge}
                        role="button"
                        tabIndex={0}
                        onClick={handleRequestPermission}
                      >{t('pages.NotificationSettings.enable')}
                      </IonBadge>
                    )}
                  </IonItem>
                  <IonItem className={styles.settingsItem} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                      <IonIcon icon={mailOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.emailNotifications')}</h3>
                      <p>{t('pages.NotificationSettings.receiveNotificationsViaEmail')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={emailEnabled}
                      onIonChange={(e) => { setEmailEnabled(e.detail.checked); savePrefs({ email: e.detail.checked }); }}
                    />
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Alert Preferences */}
            <IonCard className={styles.sectionCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.sectionTitle}>
                  <IonIcon icon={volumeHighOutline} className={styles.sectionIcon} />{t('pages.NotificationSettings.alertPreferences')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList className={styles.settingsList}>
                  <IonItem className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                      <IonIcon icon={volumeHighOutline} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.sound')}</h3>
                      <p>{t('pages.NotificationSettings.playSoundForNotifications')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={soundEnabled}
                      onIonChange={(e) => { setSoundEnabled(e.detail.checked); savePrefs({ sound: e.detail.checked }); }}
                      disabled={!pushEnabled}
                    />
                  </IonItem>
                  <IonItem className={styles.settingsItem} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={phonePortraitSharp} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.vibration')}</h3>
                      <p>{t('pages.NotificationSettings.vibrateForNotifications')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={vibrationEnabled}
                      onIonChange={(e) => { setVibrationEnabled(e.detail.checked); savePrefs({ vibration: e.detail.checked }); }}
                      disabled={!pushEnabled}
                    />
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>

            {/* Notification Categories */}
            <IonCard className={styles.sectionCard}>
              <IonCardHeader>
                <IonCardTitle className={styles.sectionTitle}>
                  <IonIcon icon={listOutline} className={styles.sectionIcon} />{t('pages.NotificationSettings.notificationCategories')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonList className={styles.settingsList}>
                  <IonItem className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                      <IonIcon icon={calendar} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.bookings')}</h3>
                      <p>{t('pages.NotificationSettings.newBookingsCancellationsAndReschedules')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={bookingNotifs}
                      onIonChange={(e) => { setBookingNotifs(e.detail.checked); savePrefs({ categories: { bookings: e.detail.checked } }); }}
                    />
                  </IonItem>
                  <IonItem className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSuccess}`}>
                      <IonIcon icon={cash} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.payments')}</h3>
                      <p>{t('pages.NotificationSettings.paymentConfirmationsAndReceipts')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={paymentNotifs}
                      onIonChange={(e) => { setPaymentNotifs(e.detail.checked); savePrefs({ categories: { payments: e.detail.checked } }); }}
                    />
                  </IonItem>
                  <IonItem className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                      <IonIcon icon={people} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.teamUpdates')}</h3>
                      <p>{t('pages.NotificationSettings.teamMemberActivityAndChanges')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={teamNotifs}
                      onIonChange={(e) => { setTeamNotifs(e.detail.checked); savePrefs({ categories: { team: e.detail.checked } }); }}
                    />
                  </IonItem>
                  <IonItem className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeNeutral}`}>
                      <IonIcon icon={documentText} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.forms')}</h3>
                      <p>{t('pages.NotificationSettings.newFormSubmissions')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={formNotifs}
                      onIonChange={(e) => { setFormNotifs(e.detail.checked); savePrefs({ categories: { forms: e.detail.checked } }); }}
                    />
                  </IonItem>
                  <IonItem className={styles.settingsItem}>
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                      <IonIcon icon={warning} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.overdueInvoices')}</h3>
                      <p>{t('pages.NotificationSettings.paymentRemindersAndOverdueNotices')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={overdueNotifs}
                      onIonChange={(e) => { setOverdueNotifs(e.detail.checked); savePrefs({ categories: { overdue: e.detail.checked } }); }}
                    />
                  </IonItem>
                  <IonItem className={styles.settingsItem} lines="none">
                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                      <IonIcon icon={receiptSharp} />
                    </div>
                    <IonLabel>
                      <h3>{t('pages.NotificationSettings.autoInvoiceSent')}</h3>
                      <p>{t('pages.NotificationSettings.automaticInvoiceConfirmations')}</p>
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={invoiceNotifs}
                      onIonChange={(e) => { setInvoiceNotifs(e.detail.checked); savePrefs({ categories: { invoice: e.detail.checked } }); }}
                    />
                  </IonItem>
                </IonList>
              </IonCardContent>
            </IonCard>
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>
    );

  return onClose ? <div className={styles.modalWrapper}>{pageContent}</div> : <IonPage className={styles.page}>{pageContent}</IonPage>;
};

export default NotificationSettings;
